/* =====================================================================
   BeastLife-style Cart Drawer — behavior
   File: assets/cart-drawer-bl.js
   Handles:
     - Add-on product checkbox (AJAX add)
     - Recommendations "+ Add" buttons (AJAX add)
     - Suggested coupon "Apply" buttons
     - "View All Offers" → open discount-code accordion
     - Cart drawer refresh after any change
   ===================================================================== */

(function () {
  'use strict';

  // -------------------------- Config --------------------------
  const CART_PATHS = {
    ADD: '/cart/add.js',
    CHANGE: '/cart/change.js',
    UPDATE: '/cart/update.js',
    GET: '/cart.js',
  };

  // -------------------------- Helpers --------------------------
  function $(sel, ctx = document) { return ctx.querySelector(sel); }
  function $$(sel, ctx = document) { return Array.from(ctx.querySelectorAll(sel)); }

  async function refreshCartDrawer() {
    try {
      const url = `${window.location.pathname}${window.location.search}`;
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await res.text();
      const dom = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = dom.querySelector('cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');
      if (newDrawer && currentDrawer) {
        const wasOpen = currentDrawer.classList.contains('active') ||
                        currentDrawer.classList.contains('is-open') ||
                        currentDrawer.hasAttribute('open');
        currentDrawer.innerHTML = newDrawer.innerHTML;
        // Sync data attrs (for cart-total etc.)
        Array.from(newDrawer.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) {
            currentDrawer.setAttribute(attr.name, attr.value);
          }
        });
        // Sync is-empty class
        currentDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        bindAll(currentDrawer);
        if (wasOpen) tryOpenDrawer();
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      console.error('[BL Cart] refresh failed', e);
    }
  }

  function tryOpenDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (typeof drawer.open === 'function') { drawer.open(); return; }
    // Fallback: trigger common open patterns
    drawer.classList.add('active', 'is-open');
    drawer.setAttribute('open', '');
    document.body.classList.add('cart-drawer-open', 'overflow-hidden');
  }

  function showError(msg) {
    const errBox = $('#CartDrawer-CartErrors');
    if (errBox) {
      errBox.textContent = msg || 'Something went wrong. Please try again.';
      setTimeout(() => { errBox.textContent = ''; }, 4000);
    } else {
      console.warn('[BL Cart]', msg);
    }
  }

  async function postJSON(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let msg = `Cart error (${res.status})`;
      try { const j = await res.json(); msg = j.description || j.message || msg; } catch (e) {}
      throw new Error(msg);
    }
    return res.json();
  }

  // -------------------------- Add-on checkbox --------------------------
  function handleAddonChange(e) {
    const cb = e.target;
    const variantId = cb.dataset.variantId;
    if (!variantId) return;

    if (cb.checked) {
      cb.disabled = true;
      postJSON(CART_PATHS.ADD, { items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        .then(() => refreshCartDrawer())
        .catch(err => { cb.checked = false; showError(err.message); })
        .finally(() => { cb.disabled = false; });
    } else {
      // If unchecked, find the line and remove
      cb.disabled = true;
      fetch(CART_PATHS.GET).then(r => r.json()).then(cart => {
        const line = cart.items.findIndex(it => String(it.variant_id) === String(variantId));
        if (line >= 0) {
          return postJSON(CART_PATHS.CHANGE, { line: line + 1, quantity: 0 });
        }
      })
        .then(() => refreshCartDrawer())
        .catch(err => { cb.checked = true; showError(err.message); })
        .finally(() => { cb.disabled = false; });
    }
  }

  // -------------------------- Recommendation +Add --------------------------
  function handleRecAdd(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.textContent = 'Adding...';

    postJSON(CART_PATHS.ADD, { items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      .then(() => refreshCartDrawer())
      .catch(err => {
        btn.classList.remove('is-loading');
        btn.textContent = '+ Add';
        showError(err.message);
      });
  }

  // -------------------------- Coupon apply --------------------------
  function handleCouponApply(e) {
    const btn = e.currentTarget;
    const code = btn.dataset.blApplyCoupon;
    if (!code) return;

    btn.disabled = true;
    btn.textContent = 'Applying...';

    // Shopify pattern: POST discount via /discount/<code> redirect
    // Or use /cart/update.js with discount param (theme-dependent)
    // Most reliable cross-theme: use /discount/<code> and then refresh
    fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET', credentials: 'same-origin' })
      .then(() => refreshCartDrawer())
      .catch(err => { showError(err.message); })
      .finally(() => { btn.disabled = false; btn.textContent = 'Apply'; });
  }

  // -------------------------- View All Offers --------------------------
  function handleViewAllOffers(e) {
    e.preventDefault();
    // Find the discount toggle and open it
    const discountToggle = $('[data-bl-discount-toggle] details');
    if (discountToggle) {
      discountToggle.open = true;
      discountToggle.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const input = discountToggle.querySelector('input[type="text"], input[name*="discount"]');
      if (input) setTimeout(() => input.focus(), 250);
    } else {
      console.warn('[BL Cart] No discount toggle found.');
    }
  }

  // -------------------------- Bind all interactive elements --------------------------
  function bindAll(root = document) {
    // Add-on checkbox
    $$('[data-bl-addon-cb]', root).forEach(cb => {
      cb.removeEventListener('change', handleAddonChange);
      cb.addEventListener('change', handleAddonChange);
    });
    // Rec +Add
    $$('[data-bl-rec-add]', root).forEach(btn => {
      btn.removeEventListener('click', handleRecAdd);
      btn.addEventListener('click', handleRecAdd);
    });
    // Coupon apply
    $$('[data-bl-apply-coupon]', root).forEach(btn => {
      btn.removeEventListener('click', handleCouponApply);
      btn.addEventListener('click', handleCouponApply);
    });
    // View all offers
    $$('[data-bl-view-offers]', root).forEach(btn => {
      btn.removeEventListener('click', handleViewAllOffers);
      btn.addEventListener('click', handleViewAllOffers);
    });
  }

  // -------------------------- Optional: free gift auto-manage --------------------------
  // If you set window.BL_GIFT_PRODUCTS = { tier1: variantId, tier2: variantId, tier3: variantId }
  // and tier prices via data attrs on <cart-drawer>, this will auto add/remove free gifts
  // when cart total crosses thresholds. Disabled by default.
  async function syncFreeGifts() {
    if (!window.BL_GIFT_PRODUCTS) return;
    const drawer = document.querySelector('[data-bl-cart]');
    if (!drawer) return;

    const cart = await fetch(CART_PATHS.GET).then(r => r.json());
    const total = cart.total_price; // in paise

    const tiers = [
      { key: 'tier1', threshold: parseInt(drawer.dataset.tier1, 10) || 0, variant: window.BL_GIFT_PRODUCTS.tier1, label: 'Free Gym Shaker' },
      { key: 'tier2', threshold: parseInt(drawer.dataset.tier2, 10) || 0, variant: window.BL_GIFT_PRODUCTS.tier2, label: 'Free Gym Bag' },
      { key: 'tier3', threshold: parseInt(drawer.dataset.tier3, 10) || 0, variant: window.BL_GIFT_PRODUCTS.tier3, label: 'Free Tumbler' },
    ];

    const changes = [];
    for (const t of tiers) {
      if (!t.variant) continue;
      const inCart = cart.items.find(it => String(it.variant_id) === String(t.variant) &&
                                              it.properties && it.properties._Gifted);
      const shouldHave = total >= t.threshold;
      if (shouldHave && !inCart) {
        changes.push({ type: 'add', variant: t.variant, label: t.label });
      } else if (!shouldHave && inCart) {
        changes.push({ type: 'remove', line: cart.items.indexOf(inCart) + 1 });
      }
    }
    if (!changes.length) return;

    for (const ch of changes) {
      try {
        if (ch.type === 'add') {
          await postJSON(CART_PATHS.ADD, {
            items: [{ id: parseInt(ch.variant, 10), quantity: 1, properties: { _Gifted: ch.label } }]
          });
        } else {
          await postJSON(CART_PATHS.CHANGE, { line: ch.line, quantity: 0 });
        }
      } catch (e) { console.warn('[BL Gift]', e); }
    }
    await refreshCartDrawer();
  }

  // Run gift sync on cart updates (debounced)
  let _giftTimer = null;
  function scheduleGiftSync() {
    if (!window.BL_GIFT_PRODUCTS) return;
    clearTimeout(_giftTimer);
    _giftTimer = setTimeout(syncFreeGifts, 400);
  }

  // -------------------------- Init --------------------------
  function init() {
    bindAll(document);
    scheduleGiftSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for theme integration
  window.BLCart = {
    refresh: refreshCartDrawer,
    bindAll: bindAll,
    syncFreeGifts: syncFreeGifts,
  };

  // Listen for cart change events from theme's existing JS
  document.addEventListener('cart:update', () => { scheduleGiftSync(); });
  document.addEventListener('cart:refresh', () => { scheduleGiftSync(); });

  // Re-bind whenever cart-drawer content gets replaced by theme's own JS
  const observerTarget = document.querySelector('cart-drawer');
  if (observerTarget) {
    const mo = new MutationObserver(() => { bindAll(observerTarget); });
    mo.observe(observerTarget, { childList: true, subtree: true });
  }
})();
